import {
  Injectable,
  inject,
} from '@angular/core';
import {UserAclService} from '../user-acl/user-acl.service';
import {FeatureAclService} from '../feature-acl/feature-acl.service';
import {JwtService} from '../../jwt/jwt.service';
import {INonLibraryAccessFilter} from '../../../../../models/default-filter.model';
import {isArrayContainsAtLeastOneElementFromArray} from '../../../../../common/functions/arrays';
import {IframeAuthService} from '../../iframe-auth/iframe-auth.service';
import {PageType} from '../../../../../common/enums/page-type.enum';

@Injectable({
  providedIn: 'root',
})
export class LibraryAclService {

  private userAclService = inject(UserAclService);

  private featureAclService = inject(FeatureAclService);

  private iframeAuthService = inject(IframeAuthService);

  private jwtService = inject(JwtService);

  /**
   * Method: Get params for restrict access to Library for Insurer without access library permission
   * @param defaultLoBIds
   */
  public restrictionForInsurerWithoutLibraryAccess(defaultLoBIds?: string[]): INonLibraryAccessFilter {
    if (this.userAclService.isInsurerRole() && !this.featureAclService.isInsurerGCC()) {
      const activeLoBs = this.jwtService.getActiveLobs();
      const preselectedInsurers = [this.jwtService.getOwnCompanyId()];
      const disableNonPreselectedInsurers = true;
      const preselectedContractTypes = this.jwtService.getContractTypes();
      return {
        activeLoBs,
        preselectedInsurers,
        disableNonPreselectedInsurers,
        preselectedContractTypes,
        defaultLoBIds: defaultLoBIds && isArrayContainsAtLeastOneElementFromArray(activeLoBs, defaultLoBIds)
          ? defaultLoBIds : [],
      };
    }
    return null;
  }

  /**
   * Method: Check access to tab Report in Library
   */
  public hasInsurerLibraryAccess(): boolean {
    if (this.iframeAuthService.hasIframeToken()) {
      return this.iframeAuthService.isInsurerGCC();
    }
    return this.userAclService.isInsurerRole() || this.userAclService.isGuestRole()
      ? this.featureAclService.isInsurerGCC()
      : true;
  }

  /**
   * Method: Check access to tab Inter-Pension in Library
   */
  hasInterPensionAccess(): boolean {
    return this.userAclService.isApplicationAdmin()
      || this.userAclService.isLibraryAdmin()
      || (this.userAclService.isInsurerAdmin() && this.featureAclService.hasInterPensionAccess());
  }

  /**
   * Method: Detect tooltip for tab Report
   */
  public hasTooltipForTabReportInLibrary(): boolean {
    if (this.iframeAuthService.hasIframeToken()) {
      return this.iframeAuthService.isInsurerRoleType() && !this.iframeAuthService.isInsurerGCC();
    }
    return (this.userAclService.isInsurerRole() || this.userAclService.isGuestRole())
      && !this.featureAclService.isInsurerGCC();

  }

  /**
   * Method: Check access to tab Factsheet
   */
  isFactsheetEnabled(): boolean {
    return this.featureAclService.isFactsheetEnabled() || this.userAclService.isApplicationAdmin();
  }

  /**
   * Method: Check access to tab Pension Report
   */
  isPensionReportEnabled(): boolean {
    return this.featureAclService.isPensionReportEnabled() || this.userAclService.isApplicationAdmin();
  }

  /**
   * Method: Check access to Interview full JSON export functionality
   */
  hasInterviewFullJsonExportAccess(): boolean {
    return this.featureAclService.hasInterviewFullJsonExportAccess();
  }

  /**
   * Method: Check access to Library or Offer Comparison
   */
  public isLibraryOrOfferComparisonAccess(pageType: PageType): boolean {
    return pageType === PageType.LIBRARY || pageType === PageType.OFFER_COMPARISON;
  }

}
